// src/SavedScreen.js
import React, { useState, useMemo } from 'react';
import { B, VIBES } from './constants';
import DestinationCard from './components/DestinationCard';
import EmptyState from './components/ui/EmptyState';
import Footer from './components/Footer';

const VIEW_MODES = [
  { id: 'all', label: 'All Saved', emoji: '📋' },
  { id: 'collections', label: 'Collections', emoji: '📂' },
];

const COLLECTION_COLORS = {
  relaxing: { bg: 'rgba(78,203,141,0.12)', border: B.mint, icon: '🌴' },
  adventurous: { bg: 'rgba(245,166,35,0.12)', border: B.amber, icon: '🧗' },
  romantic: { bg: 'rgba(233,30,99,0.12)', border: '#e91e63', icon: '💕' },
  cultural: { bg: 'rgba(156,39,176,0.12)', border: '#9c27b0', icon: '🏛️' },
  foodie: { bg: 'rgba(255,87,34,0.12)', border: '#ff5722', icon: '🍜' },
  wellness: { bg: 'rgba(0,188,212,0.12)', border: '#00bcd4', icon: '🧘' },
  nightlife: { bg: 'rgba(63,81,181,0.12)', border: '#3f51b5', icon: '🌃' },
  wildlife: { bg: 'rgba(76,175,80,0.12)', border: '#4caf50', icon: '🦁' },
  beach: { bg: 'rgba(3,169,244,0.12)', border: '#03a9f4', icon: '🏖️' },
  mountain: { bg: 'rgba(121,85,72,0.12)', border: '#795548', icon: '🏔️' },
  urban: { bg: 'rgba(96,125,139,0.12)', border: '#607d8b', icon: '🏙️' },
  luxury: { bg: 'rgba(255,215,0,0.12)', border: '#ffd700', icon: '💎' },
};

function CollectionGroupCard({ vibe, destinations, onToggleSave, goScreen }) {
  const config = COLLECTION_COLORS[vibe] || { bg: 'rgba(107,114,128,0.12)', border: B.muted, icon: '🌍' };
  const vibeData = VIBES.find(v => v.id === vibe);
  const label = vibeData ? vibeData.label : vibe.charAt(0).toUpperCase() + vibe.slice(1);

  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20,
        padding: '14px 20px', borderRadius: 14,
        background: config.bg, border: `1px solid ${config.border}30`,
      }}>
        <span style={{ fontSize: 28 }}>{config.icon}</span>
        <div>
          <h3 style={{
            fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700,
            color: B.night,
          }}>
            {label}
          </h3>
          <p style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: B.muted,
          }}>
            {destinations.length} destination{destinations.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 }}>
        {destinations.map((dest, i) => (
          <DestinationCard key={dest.id} destination={dest} isSaved={true} onToggleSave={onToggleSave} onClick={() => goScreen('detail', dest)} delay={i * 0.06} />
        ))}
      </div>
    </div>
  );
}

export default function SavedScreen({ destinations, saved, onToggleSave, goScreen, user, onAuthRequired }) {
  const [viewMode, setViewMode] = useState('all');
  const savedDestinations = (Array.isArray(destinations) ? destinations : []).filter(d => saved.some(s => s.id === d.id));

  const collections = useMemo(() => {
    const grouped = {};
    savedDestinations.forEach(dest => {
      const vibe = dest.vibe || 'other';
      if (!grouped[vibe]) grouped[vibe] = [];
      grouped[vibe].push(dest);
    });
    return Object.entries(grouped).sort((a, b) => b[1].length - a[1].length);
  }, [savedDestinations]);

  if (!user) {
    return (
      <div>
        <EmptyState
          icon="🔒"
          title="Save your dream trips"
          description="Sign in to save destinations and access your personal travel collection from anywhere."
          action={{ label: 'Sign In to Continue', onClick: onAuthRequired }}
          variant="withIllustration"
        />
        <Footer goScreen={goScreen} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ padding:'48px 40px', minHeight:'70vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20, marginBottom: 36 }}>
          <div>
            <p style={{ color:B.forest, fontSize:11, letterSpacing:4, textTransform:'uppercase', fontWeight:700, fontFamily:"'Montserrat',sans-serif", marginBottom:8 }}>Your collection</p>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:42, fontWeight:700 }}>Saved Trips</h2>
            <p style={{ color:B.muted, marginTop:6, fontFamily:"'Montserrat',sans-serif" }}>
              Hello {user.name.split(' ')[0]}! Here are your saved adventures.
            </p>
          </div>
          {savedDestinations.length > 0 && (
            <div style={{ display: 'flex', gap: 6, background: B.sand, borderRadius: 10, padding: 4 }}>
              {VIEW_MODES.map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600,
                    background: viewMode === mode.id ? 'white' : 'transparent',
                    color: viewMode === mode.id ? B.forest : B.muted,
                    boxShadow: viewMode === mode.id ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  {mode.emoji} {mode.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {savedDestinations.length === 0 ? (
          <EmptyState
            icon="📭"
            title="No saved trips yet"
            description="Start exploring destinations and save your favorites to build your travel collection."
            action={{ label: 'Explore Destinations', onClick: () => goScreen('home') }}
            variant="withIllustration"
          />
        ) : viewMode === 'all' ? (
          <>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:B.muted, marginBottom:24 }}>
              {savedDestinations.length} trip{savedDestinations.length!==1?'s':''} saved
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:24 }}>
              {savedDestinations.map((dest, i) => (
                <DestinationCard key={dest.id} destination={dest} isSaved={true} onToggleSave={onToggleSave} onClick={() => goScreen('detail', dest)} delay={i*0.08} />
              ))}
            </div>
          </>
        ) : (
          <>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:B.muted, marginBottom:24 }}>
              {collections.length} collection{collections.length!==1?'s':''} with {savedDestinations.length} destination{savedDestinations.length!==1?'s':''}
            </p>
            {collections.map(([vibe, dests]) => (
              <CollectionGroupCard
                key={vibe}
                vibe={vibe}
                destinations={dests}
                onToggleSave={onToggleSave}
                goScreen={goScreen}
              />
            ))}
          </>
        )}
      </div>
      <Footer goScreen={goScreen} />
    </div>
  );
}
